
'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getSupabaseBrowser } from '@/lib/supabase-browser';
import { DataRefreshCtx } from './data-refresh-ctx';

type Org = { id: string; name: string } | null;

type OrgCtx = {
  current_org_id: string | null;
  org: Org;
  setCurrentOrgId: (id: string) => void;
  loading: boolean;
  timedOut: boolean;
  error: string | null;
};

const Ctx = createContext<OrgCtx>({
  current_org_id: null,
  org: null,
  setCurrentOrgId: () => {},
  loading: true,
  timedOut: false,
  error: null,
});

const STORAGE_KEY = 'current_org_id';

export function OrgProvider({ 
  children, 
  initialOrgId 
}: { 
  children: React.ReactNode;
  initialOrgId?: string | null;
}) {
  const { lastManual } = useContext(DataRefreshCtx);
  
  // 1. Inicialização Otimista: Tenta obter ID imediatamente de prop ou storage
  const [current_org_id, setCurrentOrgIdState] = useState<string | null>(() => {
    if (initialOrgId) return initialOrgId;
    if (typeof window !== 'undefined') {
      try { return localStorage.getItem(STORAGE_KEY) || null; } catch {}
    }
    return null;
  });

  const [org, setOrg] = useState<Org>(null);
  
  // Loading inicial só é true se NÃO tivermos um ID otimista
  const [loading, setLoading] = useState(!current_org_id);
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper para atualizar state e storage
  function setCurrentOrgId(id: string) {
    setCurrentOrgIdState(id);
    try { 
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, id);
        localStorage.setItem('droneops_current_org_id', id);
      } 
    } catch {}
    console.log('[OrgProvider] setCurrentOrgId', id);
  }

  // Timeout de segurança (fallback)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('[OrgProvider] Loading timeout (3s) - marcando timedOut');
        setTimedOut(true);
        setLoading(false); // Libera UI mesmo se incompleto
      }
    }, 3000);
    return () => clearTimeout(timeout);
  }, [loading]);

  // Lógica principal de validação em background
  useEffect(() => {
    let cancelled = false;

    async function validateOrgInBackground() {
      try {
        const supabase = getSupabaseBrowser();
        
        // 1. Obter usuário (apenas uma vez)
        const { data: { user } } = await supabase.auth.getUser();
        
        if (cancelled) return;

        if (!user) {
          // Sem usuário, limpar contexto e finalizar
          if (current_org_id) {
            console.log('[OrgProvider] Usuário não autenticado. Limpando org.');
            setCurrentOrgIdState(null);
            try { localStorage.removeItem(STORAGE_KEY); } catch {}
          }
          setLoading(false);
          return;
        }

        const uid = user.id;

        // 2. Se já temos ID (otimista), validar se pertence ao usuário
        if (current_org_id) {
          const { data: membership } = await supabase
            .from('org_members')
            .select('org_id')
            .eq('profile_id', uid)
            .eq('org_id', current_org_id)
            .single();

          if (membership) {
            console.log('[OrgProvider] ID otimista validado com sucesso:', current_org_id);
            setLoading(false);
            return; // Tudo certo
          } 

          console.warn('[OrgProvider] ID otimista inválido para o usuário. Buscando correto...');
        }

        // 3. Se não temos ID ou era inválido, buscar o melhor membership
        const { data: allMemberships } = await supabase
          .from('org_members')
          .select('org_id, role')
          .eq('profile_id', uid);

        if (allMemberships && allMemberships.length > 0) {
           // Prioridade: pilot > owner > admin > primeiro
           const pilot = allMemberships.find(m => m.role === 'pilot');
           const owner = allMemberships.find(m => m.role === 'owner');
           const admin = allMemberships.find(m => m.role === 'admin');
           const target = pilot || owner || admin || allMemberships[0];

           console.log('[OrgProvider] Org selecionada (auto):', target.org_id);
           setCurrentOrgId(target.org_id);
           setLoading(false);
        } else {
           // 4. Sem memberships -> Tentar Bootstrap se necessário
           console.log('[OrgProvider] Sem memberships. Possível conta nova ou erro de sync.');
           setLoading(false);
        }
      } catch (err: any) {
        console.error('[OrgProvider] Erro na validação:', err);
        setError(err.message);
        setLoading(false);
      }
    }

    // Listener de Auth para revalidar ao logar
    const { data: { subscription } } = getSupabaseBrowser().auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
         if (Date.now() - lastManual > 1000) {
            validateOrgInBackground();
         }
      }
    });

    validateOrgInBackground();

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [initialOrgId, lastManual]); 

  // Bootstrap (executa se ainda não tivermos org)
  useEffect(() => {
    let cancelled = false;
    const bootstrapKey = 'bootstrap_running';
    
    if (current_org_id || loading) return;

    if (typeof window !== 'undefined' && window.sessionStorage.getItem(bootstrapKey)) return;

    const runBootstrap = async () => {
        const supabase = getSupabaseBrowser();
        const { data: u } = await supabase.auth.getUser();
        if (!u?.user) return;

        if (typeof window !== 'undefined') window.sessionStorage.setItem(bootstrapKey, 'true');

        try {
             const r = await fetch('/api/bootstrap', { method: 'POST', credentials: 'include' });
             if (cancelled) return;

             if (r.ok) {
                 const j = await r.json().catch(() => ({}));
                 if (j?.orgId) {
                     console.log('[OrgProvider] Bootstrap created org:', j.orgId);
                     setCurrentOrgId(j.orgId);
                     if (j.redirectTo && typeof window !== 'undefined') {
                         window.location.href = j.redirectTo;
                     }
                 }
             }
        } catch (e) {
             console.error('[OrgProvider] Bootstrap failed', e);
        } finally {
             if (typeof window !== 'undefined') window.sessionStorage.removeItem(bootstrapKey);
        }
    };

    runBootstrap();

    return () => { 
        cancelled = true;
        if (typeof window !== 'undefined') window.sessionStorage.removeItem(bootstrapKey);
    };
  }, [current_org_id, loading]);

  // Carregar detalhes da Org e tratar ChunkLoadError
  useEffect(() => {
    let mounted = true;
    
    async function fetchOrgDetails() {
      if (!current_org_id) {
        if (mounted) setOrg(null);
        return;
      }

      try {
        const { getOrgById } = await import('@/lib/org-resolver');
        const { org: data } = await getOrgById(current_org_id);
        
        if (mounted) {
            if (data) setOrg(data);
        }
      } catch (error: any) {
        if (mounted) {
             const isChunkError = error?.name === 'ChunkLoadError' || 
                                  error?.message?.toLowerCase().includes('loading chunk');
             if (isChunkError) {
                 console.error('[OrgProvider] ChunkLoadError. Reloading...');
                 const key = 'chunk_reload_retry';
                 const last = parseInt(sessionStorage.getItem(key) || '0');
                 if (Date.now() - last > 15000) {
                     sessionStorage.setItem(key, Date.now().toString());
                     window.location.reload();
                 } else {
                     setError("Erro de rede. Atualize a página.");
                 }
             }
        }
      }
    }

    fetchOrgDetails();
    return () => { mounted = false; };
  }, [current_org_id]);

  const value = useMemo(() => ({ current_org_id, org, setCurrentOrgId, loading, timedOut, error }), [current_org_id, org, loading, timedOut, error]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useOrg = () => useContext(Ctx);
